import { AppString } from './appString';
import { emailRegex,  phoneNumberRegex } from './commonRegex';
import {
	councilDistrictOptions,
	zipCodeOptions,
	stateAssemblyOptions,
	stateSenateOptions,
	communityDistrictOptions,
	propertyMapping,
	boroughList,
} from '@mockData';
import {
	ContactInfo,
	PersonInfo,
	DatasetMetadata,
	ApiResponse,
	TransformedData,
	HousingOption,
	Filter,
	FilterObject,
} from '@interface';
import {
	communityDistrictsJson,
	councilDistrictsJson,
	stateAssemblyJson,
	stateSenateJson,
	zipCodesJson,
} from '@/mockData/projectData/districtGeoJson';
import { toast } from 'react-toastify';

const staticFieldMappings: { [key: string]: string } = {
	bbl: 'BBL',
	address: 'Address',
	zipcode: 'Zip Code',
	yearbuilt: 'Year Built',
	unitstotal: 'Total Units',
	unitsres: 'Residential Units',
	unitsrentstabilized: 'Rent Stabilized Units',
	latestsaleprice: 'Latest sale price',
	latitude: 'latitude',
	longitude: 'longitude',
};

export const categoryValueSelector = (value: string) => {
	switch (value) {
		case 'zipcode':
			return zipCodeOptions;
		case 'council':
			return councilDistrictOptions;
		case 'community':
			return communityDistrictOptions;
		case 'state-assembly':
			return stateAssemblyOptions;
		case 'state-senate':
			return stateSenateOptions;
		case 'borough':
			return boroughList;
		default:
			return [];
	}
};

export const geoJsonValueSelector = (value: string) => {
	switch (value) {
		case 'zipcode':
			return { districtJsonVal: zipCodesJson, borderKeyVal: 'postalCode' };
		case 'council':
			return {
				districtJsonVal: councilDistrictsJson,
				borderKeyVal: 'cityCouncilDistrict',
			};
		case 'community':
			return {
				districtJsonVal: communityDistrictsJson,
				borderKeyVal: 'communityDistrict',
			};
		case 'state-assembly':
			return { districtJsonVal: stateAssemblyJson, borderKeyVal: 'AssemDist' };
		case 'state-senate':
			return { districtJsonVal: stateSenateJson, borderKeyVal: 'StSenDist' };
		default:
			return { districtJsonVal: {}, borderKeyVal: '' };
	}
};
export const validateEmail = (email: string): string => {
	if (!email) {
		return AppString.email_is_required;
	}
	if (!emailRegex.test(email)) {
		return AppString.please_enter_a_valid_email;
	}
	return '';
};

export const validatePassword = (password: string): string => {
	if (!password) {
		return AppString.password_is_required;
	}
	if (password.length < 8 && password.length > 20) {
		return AppString.password_is_weak;
	}
	return '';
};

export const validateConfirmPassword = (
	password: string,
	confirmPassword: string
): string => {
	if (!confirmPassword) {
		return AppString.confirm_password_is_required;
	}
	if (password !== confirmPassword) {
		return AppString.passwords_do_not_match;
	}
	return '';
};

export const validateUsername = (username: string): string => {
	if (!username) {
		return AppString.username_is_required;
	}
	return '';
};

export const validateFistName = (fistName: string): string => {
	if (!fistName) {
		return AppString.firstname_is_required;
	}
	return '';
};
export const validateLastName = (lastname: string): string => {
	if (!lastname) {
		return AppString.lastname_is_required;
	}
	return '';
};

export const validatePhoneNumber = (phoneNumber: string): string => {
	if (!phoneNumber) {
		return AppString.phone_number_is_required;
	}
	if (!phoneNumberRegex.test(phoneNumber)) {
		return AppString.invalid_phone_number;
	}
	return '';
};

export const generateSecureId = (length = 16) => {
	const array = new Uint8Array(length);
	window.crypto.getRandomValues(array);

	const idString = Array.from(array, (byte) => byte.toString(10)).join('');
	return Number(idString);
};

export const debounce = (func: (...args: any[]) => void, delay: number) => {
	let timer: NodeJS.Timeout;
	return (...args: any[]) => {
		clearTimeout(timer);
		timer = setTimeout(() => {
			func(...args);
		}, delay);
	};
};

export function formatDate(dateString: string | null) {
	if (!dateString) return '';
	const date = new Date(dateString);
	return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
}

export const formatCurrency = (amount: string | number | null) => {
	if (!amount) return '';
	return typeof amount === 'string' && amount.startsWith('$')
		? amount
		: `$${amount}`;
};

export function capitalizeWords(sentence: string | null) {
	return sentence
		? sentence
			.split(' ')
			.map(
				(word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
			)
			.join(' ')
		: '';
}

export const getFullForm = (sortFrom: string) =>
	({
		EW: 'Equipment Work',
		CE: 'Construction Equipment',
		EQ: 'Equipment',
		FT: 'Fuel Storage',
		FB: 'Fuel Burning',
		BL: 'Boiler',
		SF: 'Scaffold',
		OT: 'Other',
		FS: 'Fuel Storage',
		PL: 'Plumbing',
	})[sortFrom] || sortFrom;

// Common function to transform input data
export function transformData<T>(
	inputArray: T[],
	mapper: (item: T) => Record<string, any>
) {
	return inputArray.map(mapper);
}

// Specific transformation functions
export function transformAcrisData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Document ID': item.documentid,
		'Document Date': formatDate(item.docdate),
		'Filing Date': formatDate(item.recordedfiled),
		'Document Type': item.doctype,
		Amount: formatCurrency(item.docamount),
		'Parties-1 (From)': item.acrisrealparties?.[0]?.name || '',
		'Parties-2 (From)': item.acrisrealparties?.[1]?.name || '',
	}));
}

export function transformEvictionsData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Court Index #': item.courtindexnumber,
		'Docket #': item.docketnumber,
		Date: formatDate(item.executeddate),
		Address: item.evictionaddress,
	}));
}

export function transformHPDComplentesData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Complaint ID': item.complaintid,
		'Complaint Status':
			item.status?.toLowerCase() === 'close' ? 'Closed' : 'Open',
		'Date Received': item.receiveddate,
		'Apt.': item.apartment,
		'Problem ID': item.problemid,
		'Problem Status':
			item.problemstatus?.toLowerCase() === 'close' ? 'Closed' : 'Open',
		Urgency: item.type,
		'Space Type': item.spacetype,
		'Major Category': item.majorcategory,
		'Minor Category': item.minorcategory,
		Descriptor: item.code,
		'Status Description': item.statusdescription,
	}));
}

export function transformHPDViolationsData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Violation ID': item.violationid,
		'Date Approved': formatDate(item.approveddate),
		Apartment: item.apartment,
		Floor: item.story,
		Class: item.class_name,
		Description: item.novdescription,
		'Violation Status':
			item.violationstatus?.toLowerCase() === 'close' ? 'Closed' : 'Open',
	}));
}

export function transformDOBComplentesData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Complaint #': item.complaintnumber,
		'Date Entered': formatDate(item.dateentered),
		Category: item.complaintcategory,
		'Disposition Code': item.dispositioncode,
		Unit: item.unit,
		Status: item.status?.toLowerCase() === 'closed' ? 'Closed' : 'Active',
	}));
}

export function transformECBViolationsData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Violation #': item.ecbviolationnumber,
		'Date Issued': formatDate(item.issuedate),
		'Violation Type': item.violationtype,
		Severity: item.severity,
		'Penalty Imposed': formatCurrency(item.penalityimposed),
		'Amount Paid': formatCurrency(item.amountpaid),
		'Standard Description': capitalizeWords(item.sectionlawdescription1),
		'Violation Description': capitalizeWords(item.violationdescription),
		Status: item.ecbviolationstatus === 'RESOLVE' ? 'Resolved' : 'Active',
		'Aggravated Level': item.aggravatedlevel,
		'Hearing Status': capitalizeWords(item.hearingstatus),
	}));
}

export function transformDOBViolationsData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Violation #': item.isndobbisviol,
		'Date Issued': formatDate(item.issuedate),
		'Violation Type': item.violationtype,
		Description: capitalizeWords(item.description),
		Status: capitalizeWords(item.violationcategory),
	}));
}

export function transformdobFiledPermitsData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Job Filing #': item.jobfilingnumber,
		'Date Filed': formatDate(item.datefiled),
		'Job Type': item.jobtype,
		Description: capitalizeWords(item.jobdescription),
		Status: capitalizeWords(item.jobstatus),
	}));
}

export function transformdobIssuedPermitsData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Job #': item.jobfilingnumber,
		'Work Permit': item.workpermit,
		'Date Issued': formatDate(item.issuedate),
		'Work Type': getFullForm(item.worktype),
		Description: item.jobdescription,
		'Permit Type': getFullForm(item.permit_type),
		'Permit Subtype': getFullForm(item.permit_subtype),
		'Filing Status': capitalizeWords(item.filing_status),
	}));
}

export function transformHousingLitigationsData(inputArray: any[]) {
	return transformData(inputArray, (item) => ({
		'Litigation ID': item.litigationid,
		'Date Open': formatDate(item.caseopendate),
		'Case Type': capitalizeWords(item.casetype),
		Respondent: item.respondent,
		'Open Judgement?': item.openjudgement,
		'Finding of Harassment': capitalizeWords(item.findingofharassment),
		'Finding Date': formatDate(item.findingdate),
		Penalty: formatCurrency(item.penalty),
		Status: capitalizeWords(item.casestatus),
	}));
}

export function transformCustomSearchData(inputArray: any[]) {
	return transformData(inputArray, (item) => {
		const obj: Record<string, any> = {};
		Object.keys(staticFieldMappings).forEach((key) => {
			if (key in item) {
				const mappedKey = staticFieldMappings[key]; // Use correct mapping
				obj[mappedKey] = item[key];
			}
		});

		return obj;
	});
}


export function getComunityDistrictLabel(val: string) {
	const array = communityDistrictOptions.filter((d) => d.value == val);
	return array[0].label;
}

export const convertToYearlyUnits = (
	rentData: Record<string, any>
): { year: number; units: number }[] => {
	return Object.entries(rentData)
		.filter(
			([key, value]) =>
				key.startsWith('uc') && !isNaN(Number(key.slice(2))) && value !== null
		)
		.map(([key, value]) => ({
			year: Number(key.slice(2)), // Extracting the year from "ucYYYY"
			units: value,
		}))
		.sort((a, b) => b.year - a.year); // Sorting by year in descending order
};

export const getPercentageDifference = (
	oldValue: number,
	newValue: number
): string => {
	if (oldValue === 0) return 'N/A'; // Avoid division by zero

	const difference = newValue - oldValue;
	const percentage = (difference / Math.abs(oldValue)) * 100;

	return `${percentage > 0 ? '+' : ''}${percentage.toFixed(2)}%`;
};

const formatAddress = (contact: ContactInfo): string => {
	if (
		!contact.businesshousenumber ||
		!contact.businessstreetname ||
		!contact.businesscity ||
		!contact.businessstate ||
		!contact.businesszip
	) {
		return 'n/a';
	}
	return `${contact.businessapartment ? contact.businessapartment + ',' : ''} ${contact.businesscity}, ${contact.businessstate} ${contact.businesszip}`;
};

export const transformContacts = (contacts: ContactInfo[]): PersonInfo[] => {
	return contacts
		.filter((contact) => contact.type) // Skip contacts with null type
		.map((contact) => ({
			role: contact.type!,
			name: `${contact.fistName ?? ''} ${contact.lastname ?? ''}`.trim(),
			title: contact.title || 'n/a',
			business:
				contact.businesshousenumber && contact.businessstreetname
					? `${contact.businesshousenumber} ${contact.businessapartment ?? ''} ${contact.businessstreetname}`.trim()
					: 'n/a',
			address: formatAddress(contact),
		}));
};

export function getDatasetMetadata(
	data: DatasetMetadata[],
	datasetName: string
) {
	try {
		const tabObject: Record<string, string> = {
			salesAndFinancing: 'AcrisRealMaster',
			marshalEvictions: 'Eviction',
			hpdComplaints: 'HPDComplaint',
			hpdViolations: 'HPDViolation',
			dobComplaints: 'DOBComplaint',
			dobViolations: 'DOBViolation',
			ecbViolations: 'ECBViolation',
			dobFiledPermits: 'DOBFiledPermit',
			dobIssuedPermits: 'DOBIssuedPermit',
			housingLitigations: 'HousingLitigation',
		};
		const dataset: DatasetMetadata | undefined = data.find(
			(d) => d.model_name === tabObject[datasetName]
		);
		if (!dataset) return null;
		const clonedDataset = { ...dataset };
		const dateKeys = ['records_start', 'records_end', 'last_update'];
		for (const key of dateKeys) {
			if (clonedDataset[key as keyof DatasetMetadata]) {
				clonedDataset[key as keyof DatasetMetadata] = formatDate(
					clonedDataset[key as keyof DatasetMetadata]
				);
			}
		}
		return clonedDataset;
	} catch (error) {
		console.error('Error fetching dataset metadata:', error);
		return null;
	}
}

export function transformDisrtictData(
	apiResponse: ApiResponse[],
	isProperty = false
): TransformedData[] {
	// Helper function to extract date range from field name

	const propertyCount: TransformedData = {};

	const extractDateRange = (fieldName: string): string | null => {
		const match = fieldName.match(
			/__(\d{2}\/\d{2}\/\d{4})-(\d{2}\/\d{2}\/\d{4})/
		);
		return match ? `(${match[1]} - ${match[2]})` : null;
	};

	// Helper function to identify the field type and create proper label
	const getFieldLabel = (fieldName: string): string | null => {
		const dateRange = extractDateRange(fieldName);
		if (!dateRange) return null;
		const fieldMappings: { [key: string]: string } = {
			acrisrealmasters_recent: 'Sales',
			evictions_recent: 'Marshal Evictions',
			hpdcomplaints_recent: 'HPD Problems',
			hpdviolations_recent: 'HPD Violations',
			dobcomplaints_recent: 'DOB Complaints',
			dobviolations_recent: 'DOB Violations',
			ecbviolations_recent: 'ECB Violations',
			dobfiledpermits_recent: 'DOB Permit Applications',
			dobissuedpermits_recent: 'DOB Permit Issuances',
			housinglitigations_recent: 'Litigations against landlords',
			ocahousingcourts_recent: 'Housing Court Cases',
			acrisrealmasters_lastyear: 'Sales',
			evictions_lastyear: 'Marshal Evictions',
			hpdcomplaints_lastyear: 'HPD Problems',
			hpdviolations_lastyear: 'HPD Violations',
			dobcomplaints_lastyear: 'DOB Complaints',
			dobviolations_lastyear: 'DOB Violations',
			ecbviolations_lastyear: 'ECB Violations',
			dobfiledpermits_lastyear: 'DOB Permit Applications',
			dobissuedpermits_lastyear: 'DOB Permit Issuances',
			housinglitigations_lastyear: 'Litigations against landlords',
			ocahousingcourts_lastyear: 'Housing Court Cases',
			acrisrealmasters_last3years: 'Sales',
			evictions_last3years: 'Marshal Evictions',
			hpdcomplaints_last3years: 'HPD Problems',
			hpdviolations_last3years: 'HPD Violations',
			dobcomplaints_last3years: 'DOB Complaints',
			dobviolations_last3years: 'DOB Violations',
			ecbviolations_last3years: 'ECB Violations',
			dobfiledpermits_last3years: 'DOB Permit Applications',
			dobissuedpermits_last3years: 'DOB Permit Issuances',
			housinglitigations_last3years: 'Litigations against landlords',
			ocahousingcourts_last3years: 'Housing Court Cases',
		};

		// Extract the base field name (without date and period indicator)
		const baseFieldName = fieldName.split('__')[0];
		// const periodIndicator = baseFieldName.split('_').pop(); // recent, lastyear, or last3years

		// Only process 'recent' period fields
		// if (periodIndicator === 'recent') {
		for (const [key, label] of Object.entries(fieldMappings)) {
			if (baseFieldName === key) {
				return `${label} ${dateRange}`;
			}
		}
		// }

		return null;
	};



	return apiResponse.map((building) => {
		const transformed: TransformedData = {};
		// Add static fields
		Object.entries(staticFieldMappings).forEach(([apiKey, transformedKey]) => {
			transformed[transformedKey] = building[apiKey] as string;
		});

		// Process dynamic fields with dates
		Object.keys(building).forEach((key) => {
			const fieldLabel = getFieldLabel(key);
			if (fieldLabel) {
				isProperty &&
					(propertyCount[fieldLabel] =
						(propertyCount[fieldLabel] || 0) + building[key]);
				transformed[fieldLabel] = building[key] as string;
			}
		});

		return transformed;
	});
}

export function processAndTransformHousingData(
	buildings: ApiResponse[]
): HousingOption[] {
	// Define filters
	const filters = {
		'Rent Stabilized': (b: Record<string, any>) => b.unitsrentstabilized > 0,
		'Subsidized Housing': (b: Record<string, any>) =>
			b.subsidyprograms !== null || b.subsidy421a || b.subsidyj51,
		'Small Homes': (b: Record<string, any>) =>
			b.unitsres >= 1 && b.unitsres <= 4 && b.unitsrentstabilized === 0,
		'Market Rate': (b: Record<string, any>) =>
			!b.unitsrentstabilized &&
			!b.subsidy421a &&
			!b.subsidyj51 &&
			b.subsidyprograms === null &&
			!b.nycha,
		'Public Housing': (b: Record<string, any>) => b.nycha === true,
	};

	// Process all data and calculate total residential units
	const allData = transformDisrtictData(buildings, true);
	const totalResidentialUnits =
		buildings.reduce((sum, b) => sum + (Number(b.unitsres) || 0), 0) || 0;
	const calculatePercentage = (units: number) =>
		totalResidentialUnits
			? +((units / totalResidentialUnits) * 100).toFixed(1)
			: 0;

	// Define categories
	const categories: HousingOption[] = [
		{
			label: 'All Residential Housing',
			value: 'all',
			tooltip: false,
			units: totalResidentialUnits,
			dataCount: allData.length,
			data: allData,
		},
		...Object.entries(filters).map(([key, filterFunc]) => {
			const filteredData = transformDisrtictData(buildings.filter(filterFunc));
			const units = filteredData.reduce(
				(sum, b) => sum + b['Residential Units'],
				0
			);
			return {
				label:
					key.charAt(0).toUpperCase() +
					key
						.slice(1)
						.replace(/([A-Z])/g, ' $1')
						.trim(),
				value: key,
				tooltip: true,
				data: filteredData,
				units,
				dataCount: filteredData.length,
				percentage: calculatePercentage(units),
			};
		}),
	];
	return categories;
}

export const getDateInterval = (input: string) => {
	const regex = /\((\d{2}\/\d{2}\/\d{4}) - (\d{2}\/\d{2}\/\d{4})\)/;
	const match = input.match(regex);
	if (match) {
		const [startDateStr, endDateStr] = [match[1], match[2]];

		// Convert strings to Date objects
		const startDate = new Date(startDateStr);
		const endDate = new Date(endDateStr);

		console.log(startDate, endDate);
		// Calculate the difference in months and years
		const diffTime = endDate.getTime() - startDate.getTime();
		const diffDays = diffTime / (1000 * 60 * 60 * 24); // Convert milliseconds to days
		const diffMonths = diffDays / 30.4375; // Approximate month length

		let interval = '';
		if (diffMonths >= 14) {
			interval = '3_years';
		} else if (diffMonths >= 6 && diffMonths < 13) {
			interval = '1_year';
		} else {
			interval = '30_days';
		}
		return interval;
	}

	return 'all';
};

export const updatePropertiesCount = (
	data: any[],
	thresholds: Record<string, number>,
	selectedInterval: string
) => {
	// Initialize a new properties object
	const updatedProperties: Record<string, number> = {
		sales: 0,
		'hpd-complaints': 0,
		'hpd-violations': 0,
		'dob-complaints': 0,
		'dob-permits': 0,
	};
	// Iterate over each record in the dataset
	data.forEach((record) => {
		Object.keys(record).forEach((key) => {
			const match = key.match(
				/^(.*) \((\d{2}\/\d{2}\/\d{4} - \d{2}\/\d{2}\/\d{4})\)$/
			);

			if (match) {
				const baseName = match[1]; // Extract base key like "DOB Complaints"
				const mappedKey = propertyMapping[baseName]; // Convert to mapped key
				const value = record[key]; // Get numeric value

				if (mappedKey && thresholds[mappedKey] !== undefined) {
					const interval = getDateInterval(key); // Get the interval type

					if (interval === selectedInterval &&  value >= thresholds[mappedKey] ) {
						updatedProperties[mappedKey] += 1; // Increment count
					}
				}
			}
		});
	});
	return updatedProperties;
};

export const updateSinglePropertyCount = (
	data: any[],
	thresholds: Record<string, number>,
	selectedInterval: string,
	filterKey: string
) => {
	// Get the mapped key from propertyMapping
	const mappedKey = Object.entries(propertyMapping).find(
		([_, value]) => value === filterKey
	)?.[0];

	// Return empty result if the filterKey is invalid
	if (!mappedKey || !thresholds[filterKey]) return { count: 0, data: [] };

	let count = 0;
	// Iterate only relevant data
	data.forEach((record) => {
		for (const key in record) {
			if (key.startsWith(mappedKey)) {
				const interval = getDateInterval(key);
				if (
					interval === selectedInterval &&
					record[key] >= thresholds[filterKey]
				) {
					count++;
					break; // Stop checking once a match is found for this record
				}
			}
		}
	});

	return count;
};

export const checkActiveFilters = (
	toggleStates: Record<string, boolean>,
	thresholds: Record<string, number>
) => {
	const activeFilters = Object.entries(toggleStates)
		.filter(([_, isActive]) => isActive)
		.map(([id]) => ({
			id,
			threshold: thresholds[id],
		}));

	return activeFilters;
};

export const applyFilters = (
	districtData: any[],	
	activeFilters: { id: string; threshold: number }[],
	filterType: 'AND' | 'OR',
	interval: string
): Promise<any[]> => {
	const filteredData = districtData.filter((item) => {
		const checkFilter = (filter: { id: string; threshold: number }) => {
			// Get the mapped key from propertyMapping
			const mappedKey = Object.keys(propertyMapping).find(
				(key) => propertyMapping[key] === filter.id
			);

			if (!mappedKey) return false; // Skip if no mapping found

			// Find matching field in districtData that contains the mapped key
			const matchingKey = Object.keys(item).find(
				(key) => key.includes(mappedKey) && getDateInterval(key) === interval
			);

			return matchingKey ? item[matchingKey] >= filter.threshold : false;
		};
		return filterType === 'AND'
			? activeFilters.every(checkFilter)
			: activeFilters.some(checkFilter);
	});
	return Promise.resolve(filteredData);
};

export const generateId = () => Math.random().toString(36).substring(2, 9);

export const getHousingParams = (
	housingFilters: Filter[]
): Promise<Record<string, any>> => {
	const obj: Record<string, any> = {};
	const typeMappings: Record<string, string> = {
		'7AProgram': 'propertyannotation__managementprogram',
		sroUnits: 'propertyannotation__legalclassb__gte',
		programs: 'subsidyprograms__programname__any',
	};

	housingFilters.forEach(
		({ type, value, condition, year, timeCondition, year2 }) => {
			if (typeMappings[type]) {
				obj[typeMappings[type]] = type === '7AProgram' ? '7A' : type === 'sroUnits' ?
					(value === true ? 1 : 0) : value;
			} else if (type === 'expiration') {
				if (Array.isArray(value) && condition === 'between') {
					const date1 = new Date(value[0]).toISOString().slice(0, 10);
					const date2 = new Date(value[1]).toISOString().slice(0, 10);
					obj['coresubsidyrecord__enddate__gte'] = date1;
					obj['coresubsidyrecord__enddate__lte'] = date2;
				} else if (!Array.isArray(value)) {
					const valueDate = new Date(value as Date).toISOString().slice(0, 10);
					if (condition === 'after') {
						obj['coresubsidyrecord__enddate__gte'] = valueDate;
					} else if (condition === 'before') {
						obj['coresubsidyrecord__enddate__lte'] = valueDate;
					}
				}
			} else if (type === 'residentialUnits') {
				const resUnitMapping: Record<string, string> = {
					between: 'unitsres__range',
					atLeast: 'unitsres__gte',
					atMost: 'unitsres__lte',
					exactly: 'unitsres__exact',
				};

				if (
					condition === 'between' &&
					Array.isArray(value) &&
					value.length === 2
				) {
					obj['unitsres__gte'] = value[0];
					obj['unitsres__lte'] = value[1];
				} else if (resUnitMapping[condition as string]) {
					obj[resUnitMapping[condition as string]] = value;
				}
			} else if (type === 'rentStabilized') {
				const rsMapping: Record<string, string> = {
					atLeast: 'rsunitslost__gte',
					atMost: 'rsunitslost__lte',
					exactly: 'rsunitslost__exact',
				};

				if (
					condition === 'between' &&
					Array.isArray(value) &&
					value.length === 2
				) {
					obj['rsunitslost__gte'] = ((value[0] as number) / 100).toFixed(2);
					obj['rsunitslost__lte'] = ((value[1] as number) / 100).toFixed(2);
				} else if (rsMapping[condition as string]) {
					obj[rsMapping[condition as string]] = (
						(value as number) / 100
					).toFixed(2);
				}

				if (timeCondition === 'since' && year) {
					obj['rsunitslost__start'] = year;
				} else if (timeCondition === 'between' && year && year2) {
					obj['rsunitslost__start'] = year;
					obj['rsunitslost__end'] = year2;
				} else if (timeCondition === 'before' && year) {
					obj['rsunitslost__end'] = year;
				}
			}
		}
	);

	return Promise.resolve(obj);
};
export const getAddFilterParams = (filters: any[], logicalOperator: 'AND' | 'OR') => {
	// Define mapping of condition types to their corresponding query suffixes
	const conditionTypeMap = {
		'exactly': 'exact',
		'atLeast': 'gte',
		'atMost': 'lte',
		'greaterThan': 'gt',
		'lessThan': 'lt'
	};

	// Define mapping of filter types to their database fields and date fields
	const filterTypeMap: Record<string, any> = {
		'hpdViolations': {
			countPrefix: 'hpdviolations__count',
			dateField: 'hpdviolations__approveddate',
			optionFields: {
				class: { field: 'hpdviolations__class_name', operator: '' },
				status: { field: 'hpdviolations__violationstatus', operator: '' }
			}
		},
		'dobViolations': {
			countPrefix: 'dobviolations__count',
			dateField: 'dobviolations__issuedate',
			optionFields: {
				status: { field: 'dobviolations__violationcategory', operator: '__icontains' }
			}
		},
		'ecbViolations': {
			countPrefix: 'ecbviolations__count',
			dateField: 'ecbviolations__issuedate',
			optionFields: {
				status: { field: 'ecbviolations__ecbviolationstatus', operator: '__icontains' }
			}
		},
		'hpdComplaints': {
			countPrefix: 'hpdcomplaints__count',
			dateField: 'hpdcomplaints__receiveddate',
			optionFields: {
				status: { field: 'hpdcomplaints__problemstatus', operator: '' }
			}
		},
		'dobComplaints': {
			countPrefix: 'dobcomplaints__count',
			dateField: 'dobcomplaints__dateentered',
			optionFields: {
				status: { field: 'dobcomplaints__status', operator: '' }
			}
		},
		'dobPermitApplications': {
			countPrefix: 'dobfiledpermits__count',
			dateField: 'dobfiledpermits__datefiled',
			optionFields: {
				type: { field: 'dobfiledpermits__jobtype', operator: '__icontains' }
			}
		},
		'dobPermitsIssued': {
			countPrefix: 'dobissuedpermits__count',
			dateField: 'dobissuedpermits__issuedate',
			optionFields: {
				type: { field: 'dobissuedpermits__jobdescription', operator: '__icontains' }
			}
		},
		'marshalEvictions': {
			countPrefix: 'evictions__count',
			dateField: 'evictions__executeddate',
			optionFields: {
				type: { field: 'evictions__residentialcommercial', operator: '' }
			}
		},
		'litigations': {
			countPrefix: 'housinglitigations__count',
			dateField: 'housinglitigations__caseopendate',
			optionFields: {}
		},
		'foreclosureFilings': {
			countPrefix: 'acrisreallegals__documentid__count',
			dateField: 'acrisreallegals__documentid__docdate',
			optionFields: {
				salePrice: {
					field: 'acrisreallegals__documentid__count',
					operator: '',
					valueField: 'saleAmount'
				}
			}
		},
		'taxLien': {
			countPrefix: 'taxliens__count',
			booleanField: true
		},
		'noHarassment': {
			countPrefix: 'conhrecords__count',
			booleanField: true
		},
		'alternativeEnforcement': {
			countPrefix: 'aepbuildings__count',
			booleanField: true,
			optionFields: {
				status: { field: 'aepbuildings__currentstatus', operator: '__icontains' }
			}
		}
	};

	// Process filters and build query parts
	const transformedFilters = filters.map((filter, index) => {
		const queryParts: string[] = [];
		const filterType = filterTypeMap[filter?.filterType as string]

		if (!filterType) return '';

		// Handle boolean fields (like taxLien, noHarassment)
		if (filterType.booleanField) {
			queryParts.push(`${filterType.countPrefix}__gte=${filter.value === 'yes' ? 1 : 0}`);

			// Add options for boolean fields if they exist
			if (filter.options && filterType.optionFields) {
				for (const [key, config] of Object.entries(filterType.optionFields)) {
					const configObj = config as { valueField: string, field: string; operator: string };
					if (filter.options[key]) {
						queryParts.push(`${configObj.field}${configObj.operator}=${filter.options[key]}`);
					}
				}
			}
		}
		// Handle count-based filters
		else if (filter.conditionType && conditionTypeMap[filter.conditionType as keyof typeof conditionTypeMap]) {
			const suffix = conditionTypeMap[filter.conditionType as keyof typeof conditionTypeMap];
			queryParts.push(`${filterType.countPrefix}__${suffix}=${filter.count}`);

			// Handle date conditions
			if (filter.timeCondition && filterType.dateField) {
				if (filter.timeCondition === 'since') {
					queryParts.push(`${filterType.dateField}__gte=${filter.date}`);
				} else if (filter.timeCondition === 'between') {
					queryParts.push(`${filterType.dateField}__gte=${filter.date}`);
					queryParts.push(`${filterType.dateField}__lte=${filter.date}`);
				} else if (filter.timeCondition === 'before') {
					queryParts.push(`${filterType.dateField}__lte=${filter.date}`);
				}
			}

			// Handle additional options
			if (filter.options && filterType.optionFields) {
				for (const [key, config] of Object.entries(filterType.optionFields)) {
					const configObj = config as { valueField: string, field: string; operator: string };

					if (filter.options[key]) {
						if (key === 'salePrice' && conditionTypeMap[filter.options[key] as keyof typeof conditionTypeMap]) {
							// Special handling for salePrice in foreclosureFilings
							const priceSuffix = conditionTypeMap[filter.options[key] as keyof typeof conditionTypeMap];
							queryParts.push(`${configObj.field}__${priceSuffix}=${filter.options[configObj.valueField]}`);
						} else {
							queryParts.push(`${configObj.field}${configObj.operator}=${filter.options[key]}`);
						}
					}
				}
			}
		}
		return `filter_${index}=${queryParts.join(',')}`;
	}).filter(Boolean); // Remove any empty filter strings

	return { q: `condition_0=${logicalOperator} ${transformedFilters.join(' ')}` };
};

export const getHousingValue = (value: string) => {
	switch (value) {
		case 'all':
			return { housingtype: 'all' };
		case 'rent':
			return { housingtype: 'rs' };
		case 'subsidized':
			return { housingtype: 'rr' };
		case 'small':
			return { housingtype: 'sh' };
		case 'market':
			return { housingtype: 'mr' };
		case 'public':
			return { housingtype: 'ph' };
		default:
			return { housingtype: 'all' };
	}
}

export const toastSuccessHelper = (val: string) => {
	toast.success(val, {
		position: 'top-right',
		autoClose: 4000,
		hideProgressBar: false,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
	});
}

export const generateFilterString = (filters: FilterObject[], logicalOperator: 'AND' | 'OR'): string => {
	return filters
		.map((filter) => {
			const { filterType, conditionType, count, timeCondition, date, endDate, options } = filter;

			// Base string for the filter
			let filterString = `have ${conditionType} ${count} ${filterType}`;

			// Add options if they exist
			if (options.class) {
				filterString += ` (Class ${options.class})`;
			}
			if (options.status) {
				filterString += ` (Status ${options.status})`;
			}
			if (options.type) {
				filterString += ` (Type ${options.type})`;
			}
			if (options.salePrice) {
				filterString += ` (${options.salePrice} $${options.saleAmount || 0})`;
			}

			// Add time condition
			if (timeCondition === 'since') {
				filterString += ` since ${date}`;
			} else if (timeCondition === 'before') {
				filterString += ` before ${date}`;
			} else if (timeCondition === 'between') {
				filterString += ` between ${date} and ${endDate}`;
			}

			return filterString;
		})
		.join(` ${logicalOperator} `);
};

export const genrateHousingString = (
	housingFilters: Filter[]
): Promise<string> => {
	let housingDisplayString = '';

	housingFilters.forEach(
		({ type, value, condition, year, timeCondition, year2 }) => {
			if (type === 'expiration') {
				housingDisplayString = housingDisplayString + ' ' + type;
				if (Array.isArray(value) && condition === 'between') {
					const date1 = new Date(value[0]).toISOString().slice(0, 10);
					const date2 = new Date(value[1]).toISOString().slice(0, 10);
					housingDisplayString = housingDisplayString + ' ' + condition + ' ' + date1 + ' and ' + date2 + ',';
				} else if (!Array.isArray(value)) {
					const valueDate = new Date(value as Date).toISOString().slice(0, 10);
					housingDisplayString = housingDisplayString + ' ' + condition + ' ' + valueDate + ',';
				}
			} else if (type === 'residentialUnits') {
				const resUnitMapping: Record<string, string> = {
					between: 'unitsres__range',
					atLeast: 'unitsres__gte',
					atMost: 'unitsres__lte',
					exactly: 'unitsres__exact',
				};

				if (
					condition === 'between' &&
					Array.isArray(value) &&
					value.length === 2
				) {
					housingDisplayString = housingDisplayString + ' ' + type + ' ' + condition + ' ' + value[0] + ' and ' + value[1] + ',';
				} else if (resUnitMapping[condition as string]) {
					housingDisplayString = housingDisplayString + ' ' + type + ' ' + condition + ' ' + value + ',';
				}
			} else if (type === 'rentStabilized') {
				const rsMapping: Record<string, string> = {
					atLeast: 'rsunitslost__gte',
					atMost: 'rsunitslost__lte',
					exactly: 'rsunitslost__exact',
				};

				if (
					condition === 'between' &&
					Array.isArray(value) &&
					value.length === 2
				) {
					housingDisplayString = housingDisplayString + ' ' + type + ' ' + condition + ' ' + value[0] + ' and ' + value[1] + ',';
				} else if (rsMapping[condition as string]) {
					housingDisplayString = housingDisplayString + ' ' + type + ' ' + condition + ' ' + value + ',';
				}

				if ((timeCondition === 'since' || timeCondition === 'before') && year) {
					housingDisplayString = housingDisplayString + ' ' + timeCondition + ' ' + year + ',';
				}
				if (timeCondition === 'between' && year && year2) {
					housingDisplayString = housingDisplayString + ' ' + timeCondition + ' ' + year + ' and ' + year2 + ',';
				}
			}
		}
	);
	return Promise.resolve(housingDisplayString);
};

// Add this function to your component
export const myDashboardformatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  // Get month name
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const month = months[date.getMonth()];
  
  // Get day
  const day = date.getDate();
  
  // Get year
  const year = date.getFullYear();
  
  // Get hours (12-hour format) and determine AM/PM
  let hours = date.getHours();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  
  // Get minutes with leading zero if needed
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  // Combine all parts
  return `${month} ${day} ${year} ${hours}:${minutes} ${ampm}`;
};